<template>
    <div
        ref="dropdown"
        class="Dropdown"
        :tabindex="tabIndex"
        :class="{'Dropdown--open':opened,
                 'Dropdown--disabled': disabled || !hasAvailableOptions,
                 'Dropdown--enabledClear': showClear}"
    >
        <div class="Dropdown__containerWrapper">
            <div class="Dropdown__container" @click="toggle">
                <div class="Dropdown__selectedContainer">
                    <div v-if="$slots.prepend" class="Dropdown__selectedPrepend">
                        <slot name="prepend" />
                    </div>
                    <span class="Dropdown__valueText">{{ selectedLabel }}</span>
                </div>
                <EidIcon v-if="hasAvailableOptions" class="Dropdown__arrow" :icon-src="faAngleDown" />
            </div>
            <div
                v-if="showClear"
                class="Dropdown__clearButton"
                @click="clearSelection"
            >
                <FontAwesomeIcon :icon="faTimes" />
            </div>
        </div>
        <div v-if="opened" class="Dropdown__optionsContainer">
            <input
                v-if="searchable"
                ref="searchInput"
                v-model="searchQuery"
                class="Dropdown__searchInput"
                type="text"
                placeholder="Search..."
                @click.stop
                @mousedown.stop
            >
            <template v-if="multiSelect">
                <MultiSelectOptions
                    :options="filteredDisplayedOptions"
                    :selected="selectedOptions"
                    :is-disabled-option="isDisabledOption"
                    @select="onMultiSelect"
                    @change="applySelections"
                    @abort="abort"
                    @clear="$emit('clear', $event)"
                    @scroll="handleScroll"
                >
                    <slot name="additionalContent" />
                    <template #afterOptions>
                        <span v-if="shouldShowLoadMoreMessage" class="Dropdown__loadMoreMsg">Load more...</span>
                    </template>
                </MultiSelectOptions>
            </template>
            <template v-else>
                <SingleSelectOptions
                    :options="filteredDisplayedOptions"
                    :is-disabled-option="isDisabledOption"
                    @select="onSingleSelect"
                    @scroll="handleScroll"
                >
                    <template #afterOptions>
                        <span v-if="shouldShowLoadMoreMessage" class="Dropdown__loadMoreMsg">Load more...</span>
                    </template>
                </SingleSelectOptions>
            </template>
        </div>
    </div>
</template>
<script lang="ts">
    import isEqual from 'lodash/isEqual';
    import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

    import { faAngleDown, faTimes } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Button } from '../Button';
    import Checkbox from '../Checkbox/Checkbox.vue';
    import EidIcon from '../EidIcon/EidIcon.vue';
    import Select from '../Select/Select.vue';

    import MultiSelectOptions from './MultiSelectOptions.vue';
    import SingleSelectOptions from './SingleSelectOptions.vue';
    import { DropdownOption } from './types';

    @Component({
        emits: [ 'blur', 'change', 'clear', 'close', 'focus', 'input', 'open', 'select' ],
        components: { EidIcon, MultiSelectOptions, SingleSelectOptions, Checkbox, Select, FontAwesomeIcon, Button },
    })
    export default class Dropdown extends Vue {
        @Prop({ type: Boolean, default: false })
        private searchable!: boolean;

        @Prop({ type: Boolean, default: false })
        private multiSelect!: boolean;

        @Prop({ type: Boolean, default: false })
        private disabled!: boolean;

        @Prop({ type: Boolean, default: false })
        private disableLazyLoad!: boolean;

        // lazy load will be enabled only when the options count exceeds this threshold
        @Prop({ type: Number, default: 300 })
        private lazyLoadOptionsThreshold!: number;

        // number of options to load at a time
        @Prop({ type: Number, default: 20 })
        private lazyLoadStep!: number;

        @Prop({ type: Array, default: () => [] })
        private disabledValues!: any[];

        @Prop({ type: Array, default: () => [] })
        private excludedValues!: any[];

        @Prop({ type: Boolean, default: false })
        private enableClear!: boolean;

        @Prop({ type: Number, default: 0 })
        private tabIndex!: number;

        @Prop({ type: String, default: 'Select' })
        private placeholder!: string;

        @Prop({ type: Array, default: () => [] })
        private selected!: DropdownOption[];

        @Prop({ type: Array, default: () => [] })
        private options!: DropdownOption[];

        private selectedOptions: DropdownOption[] = [];
        private previousOptions: DropdownOption[] = [];

        private faAngleDown = faAngleDown;
        private faTimes = faTimes;
        private opened: boolean = false;
        private searchQuery: string = '';

        private displayedOptions: DropdownOption[] = this.options;
        private allOptionsLoaded: boolean = false;
        private loadedCount: number = 0;
        private lazyLoadBottomDistanceThreshold: number = 20;

        @Watch('options', { immediate: true })
        private onOptionsChanged(): void {
            if (!this.isLazyLoadEnabled) {
                this.displayedOptions = this.optionsToLoadFrom;
            } else if (this.loadedCount === 0) {
                this.loadInitialOptions();
            } else {
                this.displayedOptions = this.optionsToLoadFrom.slice(0, this.loadedCount);
            }
        }

        @Watch('selected', { immediate: true })
        private onSelectedChanged(): void {
            this.selectedOptions = this.getUniqueOptions(this.selected);
            this.previousOptions = [ ...this.selectedOptions ];
        }

        @Watch('opened', { immediate: true })
        private onOpened(): void {
            if (this.opened) {
                document.addEventListener('mouseup', this.dismissDropdownOnClickOut);
                if (this.isLazyLoadEnabled) {
                    this.loadInitialOptions();
                }
                if (this.searchable) {
                    this.$nextTick(() => {
                        (this.$refs.searchInput as HTMLInputElement)?.focus();
                    });
                }
            } else {
                document.removeEventListener('mouseup', this.dismissDropdownOnClickOut);
                this.searchQuery = '';
            }
        }

        private get availableOptions(): DropdownOption[] {
            return this.getUniqueOptions(this.options).filter(({ value }) =>
                // excludedValues is expected to be small so .some and isEqual is okay here.
                !this.excludedValues.some((excludeVal) => isEqual(excludeVal, value)),
            );
        }

        private get filteredDisplayedOptions(): DropdownOption[] {
            if (!this.searchable || !this.searchQuery) {
                return this.displayedOptions;
            }
            const q = this.searchQuery.toLowerCase();
            return this.displayedOptions.filter((opt) => opt.label.toLowerCase().includes(q));
        }

        private get selectedLabel(): string {
            return this.selectedOptions.map((option) => option.label).join(', ') || this.placeholder;
        }

        private get nonSelectedOptions(): DropdownOption[] {
            const selectedSet = new Set(this.selectedOptions.map((opt) => opt.value));
            return this.availableOptions.filter((option) => !selectedSet.has(option.value));
        }

        private get hasAvailableOptions(): boolean {
            return this.multiSelect ? this.options.length > 0 : this.nonSelectedOptions?.length > 0;
        }

        private get showClear(): boolean {
            return this.enableClear && !this.multiSelect && this.selectedOptions.length > 0;
        }

        private get disabledOptions(): DropdownOption[] {
            return this.availableOptions.filter(
                (option) => this.disabledValues.some((val) => val === option.value),
            );
        }

        private get optionsToLoadFrom(): DropdownOption[] {
            return this.multiSelect ? this.availableOptions : this.nonSelectedOptions;
        }

        private get isLazyLoadEnabled(): boolean {
            return !this.disableLazyLoad && this.options.length > this.lazyLoadOptionsThreshold;
        }

        private get shouldShowLoadMoreMessage(): boolean {
            return this.isLazyLoadEnabled && this.loadedCount < this.optionsToLoadFrom.length;
        }

        private loadInitialOptions() {
            const initialLoad = this.optionsToLoadFrom.slice(0, this.lazyLoadStep);
            this.displayedOptions = [ ...initialLoad ];
            this.loadedCount = initialLoad.length;
            if (this.loadedCount >= this.options.length) {
                this.allOptionsLoaded = true;
            }
        }

        private loadMoreOptions() {
            if (this.allOptionsLoaded) {
                return;
            }
            const nextBatch = this.optionsToLoadFrom.slice(this.loadedCount, this.loadedCount + this.lazyLoadStep);
            this.displayedOptions.push(...nextBatch);
            this.loadedCount += nextBatch.length;
            if (this.loadedCount >= this.options.length) {
                this.allOptionsLoaded = true;
            }
        }

        private handleScroll(event: Event) {
            if (this.isLazyLoadEnabled) {
                const dropdown = event.target as HTMLElement;
                const scrolledHeight = dropdown.scrollTop + dropdown.clientHeight;
                const distanceToBottom = dropdown.scrollHeight - scrolledHeight;
                if (distanceToBottom <= this.lazyLoadBottomDistanceThreshold && !this.allOptionsLoaded) {
                    this.loadMoreOptions();
                }
            }
        }

        private dismissDropdownOnClickOut(event: MouseEvent) {
            if (event.target && !(this.$refs.dropdown as Element)?.contains(event.target as Node)) {
                this.onBlur();
            }
        }

        private close(): void {
            this.opened = false;
            this.$emit('close');
        }

        private open(): void {
            if (this.hasAvailableOptions && !this.disabled) {
                this.opened = true;
                this.$emit('open');
                this.$emit('focus');
            }
        }

        private abort(): void {
            this.close();
            this.selectedOptions = [ ...this.previousOptions ];
        }

        private toggle(): void {
            this.opened ? this.close() : this.open();
        }

        private onBlur(): void {
            if (this.multiSelect) {
                this.applySelections();
            } else {
                this.abort();
            }
            this.$emit('blur');
        }

        private onSingleSelect(option: DropdownOption): void {
            this.selectedOptions = [ option ];
            this.previousOptions = [ option ];
            this.$emit('select', option);
            this.$emit('input', option);
            this.$emit('change', option);
            this.close();
        }

        private clearSelection(): void {
            this.$emit('select', []);
            this.$emit('input', []);
            this.$emit('change', []);
            this.$emit('clear');
        }

        private onMultiSelect(options: DropdownOption[]): void {
            this.selectedOptions = [ ...options ];
            this.$emit('select', options);
        }

        private applySelections(): void {
            this.previousOptions = [ ...this.selectedOptions ];
            this.$emit('input', this.selectedOptions);
            this.$emit('change', this.selectedOptions);
            this.close();
        }

        private isDisabledOption(option: DropdownOption): boolean {
            return this.disabledOptions.includes(option);
        }

        private getUniqueOptions(options: DropdownOption[]): DropdownOption[] {
            const seenLabels = new Map<string, any[]>(); // Using a Map to store label-value pairs
            const uniqueOptions: DropdownOption[] = [];

            for (const option of options) {
                // Check if the label is already in the seenLabels Map
                if (!seenLabels.has(option.label)) {
                    // If the label is not seen yet, add it with an empty Set of values
                    seenLabels.set(option.label, [ option.value ]);
                    uniqueOptions.push(option);
                } else {
                    // If label exists, get the values associated with this label
                    const labelValues = seenLabels.get(option.label) || [];

                    // If the value is not yet in the Set for this label, add it
                    if (!labelValues.some((val) => isEqual(val, option.value))) {
                        labelValues.push(option.value);
                        seenLabels.set(option.label, labelValues);
                        uniqueOptions.push(option);
                    }
                }
            }
            return uniqueOptions;
        }

        private beforeUnmount() {
            document.removeEventListener('mouseup', this.dismissDropdownOnClickOut);
        }
    }
</script>
